import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { useForm, Controller } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { accountTypeSchema, AccountTypeFormValues } from '../rules/accountTypeSchema'
import AccountTypeSelector from '@/user/account-type/AccountTypeSelector'
import { AccountType } from '@/common/types'
import { ONBOARDING_COPY } from '@/locales/en'

export type PageProps = {
    progress?: number
    defaultValues?: AccountTypeFormValues
    errors?: Partial<Record<string, string>>
    loading?: boolean
    saving?: boolean
    disabled?: boolean
    supportedAccountTypes?: AccountType[]
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*;
        may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: AccountTypeFormValues) => void
    onBack?: () => void
}

export function AccountTypeForm({
    progress = 30,
    defaultValues,
    errors: externalErrors,
    loading,
    saving,
    disabled,
    supportedAccountTypes = [AccountType.INDIVIDUAL, AccountType.INDIVIDUAL_TRUST, AccountType.COMPANY_TRUST],
    onSubmit,
    onBack,
}: ViewModelProps) {
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors: formErrors, isValid },
    } = useForm<AccountTypeFormValues>({
        resolver: yupResolver(accountTypeSchema),
        mode: 'onChange',
        defaultValues,
    })

    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues)
        }
    }, [defaultValues, reset])

    const accountType = watch('accountType')
    const isTrust = watch('isTrust') ?? false

    const displayErrors: PageProps['errors'] = externalErrors

    const isDisabled = disabled ?? (!isValid || loading || saving)

    return (
        <Onboarding
            title={ONBOARDING_COPY.accountType.title}
            isLoading={saving || loading}
            description={ONBOARDING_COPY.accountType.description}
            cta="Next"
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            onBack={onBack}
            disabled={isDisabled}
            progress={progress}
        >
            <Controller
                name="accountType"
                control={control}
                render={() => (
                    <AccountTypeSelector
                        selectedAccountType={accountType ?? null}
                        setSelectedAccountType={(value) => {
                            // Don't use fallback - null is a valid intermediate state when user has selected "Trust" but not which trust type
                            setValue('accountType', value as AccountType, { shouldValidate: true })
                        }}
                        isTrust={isTrust}
                        setIsTrust={(value) => {
                            setValue('isTrust', value, { shouldValidate: true })
                        }}
                        supportedAccountTypes={supportedAccountTypes}
                    />
                )}
            />
            {(displayErrors?.accountType || formErrors.accountType?.message) && (
                <p className="text-red-500 text-sm mt-2">
                    {displayErrors?.accountType || formErrors.accountType?.message}
                </p>
            )}
        </Onboarding>
    )
}
